import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInboxCount } from '../contexts/InboxCountContext';
import { useInboxList } from '../contexts/InboxListContext';
import { useProductBulkImportContextOptional } from '../contexts/ProductBulkImportContext';
import { useCatalogImageResolveInboxContextOptional } from '../contexts/CatalogImageResolveInboxContext';
import { useGtinDumpSyncInboxContextOptional } from '../contexts/GtinDumpSyncInboxContext';
import { isCatalogImportInboxType } from '../components/inboxCatalogImport';
import { isCatalogImageResolveInboxType } from '../components/inboxCatalogImageResolve';
import type { ImportJobStatus } from './useCatalogImportJobInbox';
import type { ImageResolveJobStatus } from './useCatalogImageResolveJobInbox';
import { catalogImportAckType } from '../utils/catalogImportInboxAck';
import { catalogImageResolveAckType } from '../utils/catalogImageResolveInboxAck';

/** Keeps nav badge totals in sync even when the inbox flyout panel is closed. */
export function useInboxBadgeCounts() {
  const { items, isJobSeen } = useInboxList();
  const { syncCounts } = useInboxCount();
  const bulkImport = useProductBulkImportContextOptional();
  const imageResolveInbox = useCatalogImageResolveInboxContextOptional();
  const dumpSyncInbox = useGtinDumpSyncInboxContextOptional();
  const inboxImportItemCount = bulkImport?.inboxImportItemCount ?? 0;
  const inboxImageResolveItemCount = imageResolveInbox?.inboxImageResolveItemCount ?? 0;
  const inboxDumpSyncItemCount = dumpSyncInbox?.inboxDumpSyncItemCount ?? 0;
  const importState = bulkImport?.importState ?? {
    importProgress: null,
    result: null,
    err: null,
    jobInFlight: false,
    jobStatus: null,
    activeJobId: null,
  };
  const trackedImportJobId = bulkImport?.trackedImportJobId ?? null;
  const [ackedImportJobs, setAckedImportJobs] = useState<Set<string>>(() => new Set());
  const [ackedImageResolveJobs, setAckedImageResolveJobs] = useState<Set<string>>(() => new Set());
  const [ackedImageResolveQueue, setAckedImageResolveQueue] = useState(false);

  const { jobStatus, activeJobId } = importState;
  const importJobId = trackedImportJobId ?? activeJobId;
  const imageResolveJob = imageResolveInbox?.job;
  const showQueueCard = imageResolveInbox?.showQueueCard ?? false;
  const wasShowingQueue = useRef(false);

  // Card reappears after dismiss (counts changed) → treat as unread again.
  useEffect(() => {
    if (showQueueCard && !wasShowingQueue.current) {
      setAckedImageResolveQueue(false);
    }
    wasShowingQueue.current = showQueueCard;
  }, [showQueueCard]);

  const isImportJobAcked = useCallback(
    (jobId: string, status: ImportJobStatus) => {
      if (!jobId) return false;
      if (ackedImportJobs.has(jobId)) return true;
      return isJobSeen(jobId, catalogImportAckType(status));
    },
    [ackedImportJobs, isJobSeen],
  );

  const isImageResolveJobAcked = useCallback(
    (jobId: string, status: ImageResolveJobStatus) => {
      if (!jobId) return false;
      if (ackedImageResolveJobs.has(jobId)) return true;
      return isJobSeen(jobId, catalogImageResolveAckType(status));
    },
    [ackedImageResolveJobs, isJobSeen],
  );

  const visibleItems = useMemo(
    () =>
      items.filter(
        (i) =>
          !(inboxImportItemCount > 0 && isCatalogImportInboxType(i.type)) &&
          !(inboxImageResolveItemCount > 0 && isCatalogImageResolveInboxType(i.type)),
      ),
    [items, inboxImportItemCount, inboxImageResolveItemCount],
  );

  const importUnseenCount = useMemo(() => {
    let n = 0;
    const jobId = importJobId;
    if (importState.importProgress && importState.jobInFlight && jobId) {
      if (!isImportJobAcked(jobId, jobStatus ?? 'RUNNING')) n += 1;
    }
    if (importState.result && jobId) {
      const st: ImportJobStatus =
        importState.result.failed > 0 && importState.result.created === 0
          ? 'FAILED'
          : 'COMPLETED';
      if (!isImportJobAcked(jobId, st)) n += 1;
    }
    if (
      importState.err &&
      (importState.importProgress || importState.result || importState.jobInFlight) &&
      jobId &&
      !isImportJobAcked(jobId, 'FAILED')
    ) {
      n += 1;
    }
    return n;
  }, [importState, importJobId, jobStatus, isImportJobAcked]);

  const imageResolveUnseenCount = useMemo(() => {
    let n = 0;
    const jobId = imageResolveJob?.jobId;
    if (jobId) {
      if (imageResolveInbox?.inFlight) {
        if (!isImageResolveJobAcked(jobId, imageResolveJob.status)) n += 1;
      }
      if (imageResolveInbox?.showResult) {
        if (!isImageResolveJobAcked(jobId, 'COMPLETED')) n += 1;
      }
    }
    // Must follow showQueueCard (not raw queueActionCount) — dismissed card stays hidden
    // while counts remain > 0; counting those = ghost badge on empty flyout.
    if (showQueueCard && !ackedImageResolveQueue) n += 1;
    return n;
  }, [
    ackedImageResolveQueue,
    imageResolveInbox,
    imageResolveJob,
    isImageResolveJobAcked,
    showQueueCard,
  ]);

  const dumpSyncUnseenCount = useMemo(() => {
    if (!dumpSyncInbox) return 0;
    if (dumpSyncInbox.inFlight || dumpSyncInbox.showResult) return 1;
    return 0;
  }, [dumpSyncInbox]);

  useEffect(() => {
    const apiUnseen = visibleItems.filter((i) => !i.seen).length;
    syncCounts({
      total:
        visibleItems.length +
        inboxImportItemCount +
        inboxImageResolveItemCount +
        inboxDumpSyncItemCount,
      unseen: apiUnseen + importUnseenCount + imageResolveUnseenCount + dumpSyncUnseenCount,
    });
  }, [
    visibleItems,
    inboxImportItemCount,
    inboxImageResolveItemCount,
    inboxDumpSyncItemCount,
    importUnseenCount,
    imageResolveUnseenCount,
    dumpSyncUnseenCount,
    syncCounts,
  ]);

  const ackImportJob = useCallback((jobId: string) => {
    setAckedImportJobs((prev) => new Set(prev).add(jobId));
  }, []);

  const ackImageResolveJob = useCallback((jobId: string) => {
    setAckedImageResolveJobs((prev) => new Set(prev).add(jobId));
  }, []);

  const isImageResolveQueueSeen = useCallback(() => ackedImageResolveQueue, [ackedImageResolveQueue]);

  const ackImageResolveQueue = useCallback(() => {
    setAckedImageResolveQueue(true);
  }, []);

  return {
    ackImportJob,
    isImportJobAcked,
    ackImageResolveJob,
    isImageResolveJobAcked,
    isImageResolveQueueSeen,
    ackImageResolveQueue,
  };
}
